
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ArrowLeft, ClockIcon, AlertTriangle, BookOpen, GraduationCap, FilePlus, Layers, RefreshCw, Database, Sparkles, Loader2, Upload } from "lucide-react";
import { generateUUID } from "@/utils/uuid";
import { categories } from "@/data/questions";
import { supabase } from "@/lib/supabase";
import { normalizeDifficulty } from "@/types/quiz";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ExamCategory, ExamPaper, ExamSection, MockTestQuestion, Question } from "@/types/exam";
import LanguageSelector from "../flashcards/LanguageSelector";
import AnkiImport from "../quiz/AnkiImport";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { PaperUploader } from "./PaperUploader";
import * as mammoth from "mammoth";
import MockTestAnkiImport from "./MockTestAnkiImport";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";

const MockTestGenerator: React.FC = () => {
  const { isMobile, isPhone, screenSize } = useIsMobile();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedExam, setSelectedExam] = useState<ExamCategory | null>(null);
  const [selectedPaper, setSelectedPaper] = useState<ExamPaper | null>(null);
  const [selectedSection, setSelectedSection] = useState<ExamSection | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [examCategories, setExamCategories] = useState<ExamCategory[]>([]);
  const [examTypeFilter, setExamTypeFilter] = useState<string>("government");
  const [currentLanguage, setCurrentLanguage] = useState("en");
  const [isLoading, setIsLoading] = useState(true);
  const [paperLoadError, setPaperLoadError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<string>("select");
  const [isAdmin, setIsAdmin] = useState(false);
  const [docFile, setDocFile] = useState<File | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [wordUploadSuccess, setWordUploadSuccess] = useState(false);
  const [extractedQuestions, setExtractedQuestions] = useState<any[]>([]);
  const navigate = useNavigate();
  const [questionImportTab, setQuestionImportTab] = useState<"anki"|"word">("anki");
  const [showPyqDialog, setShowPyqDialog] = useState(true);

  useEffect(() => {
    fetchExamCategories();
    
    // Always show the dialog when component loads
    setShowPyqDialog(true);
  }, []);

  const handleClosePyqDialog = () => {
    setShowPyqDialog(false);
  };

  const navigateToPyqMultiplier = () => {
    navigate("/pyq-multiplier");
    toast.success("Navigating to PYQ Multiplier");
    handleClosePyqDialog();
  };

  const fetchExamCategories = async () => {
    setIsLoading(true);
    setPaperLoadError(null);
    try {
      const { data, error } = await supabase
        .from('exam_categories')
        .select('*')
        .order('name');

      if (error) {
        console.error('Error fetching exam categories:', error);
        toast.error("Failed to load exam categories from database");
        setPaperLoadError("Database error: " + error.message);
        setIsLoading(false);
        return;
      }

      if (!data || data.length === 0) {
        console.log('No exam categories found');
        toast.error("No exam categories found in the database");
        setPaperLoadError("No exam categories found in the database");
      } else {
        const processedData = data.map(item => {
          let papers = item.papers || [];
          let sections = item.sections || [];

          if (typeof sections === 'string') {
            try {
              sections = JSON.parse(sections);
            } catch (e) {
              console.log('Error parsing sections JSON:', e);
              sections = [];
            }
          }

          if (papers.length === 0) {
            const defaultPaper: ExamPaper = {
              id: `default-paper-${item.id}`,
              name: `${item.name} Standard Test`,
              sections: sections,
              question_count: item.question_count || 100,
              time_minutes: item.time_minutes || 120,
              marks: item.question_count || 100,
              description: sections.length > 0 
                ? "Test with predefined sections" 
                : "Standard test configuration"
            };
            papers = [defaultPaper];
          }

          papers = papers.map((paper: any) => ({
            id: paper.id || `paper-${Math.random().toString(36).substring(7)}`,
            name: paper.name || `${item.name} Paper`,
            sections: paper.sections || sections || [],
            question_count: paper.question_count || item.question_count || 100,
            time_minutes: paper.time_minutes || item.time_minutes || 120,
            marks: paper.marks || paper.question_count || 100,
            description: paper.description || null
          }));

          return {
            ...item,
            type: (item.type as 'government' | 'school' | 'entrance') || 'government',
            papers: papers,
            sections: sections
          };
        });

        setExamCategories(processedData);
        console.log('Processed exam categories:', processedData);
      }
    } catch (err) {
      console.error('Error in fetchExamCategories:', err);
      toast.error("Failed to load exam categories");
      setPaperLoadError("Error loading data: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setSelectedExam(null);
    setSelectedPaper(null);
    setSelectedSection(null);
    toast.info(`Selected category: ${category}`);
  };

  const handleExamSelect = (exam: ExamCategory) => {
    console.log("Selected exam:", exam);
    setSelectedExam(exam);
    setSelectedSection(null);
    
    if (exam.papers && Array.isArray(exam.papers) && exam.papers.length === 1 && 
        exam.papers[0].name === `${exam.name} Standard Test`) {
      const defaultPaper = exam.papers[0];
      setSelectedPaper(defaultPaper);
      toast.info(`Selected exam: ${exam.name} with default paper configuration.`);
    } else if (exam.papers && Array.isArray(exam.papers) && exam.papers.length > 0) {
      setSelectedPaper(null);
      toast.info(`Selected exam: ${exam.name}. Please select a paper.`);
    } else {
      toast.warning(`${exam.name} doesn't have any paper structure defined.`);
    }
  };

  const handlePaperSelect = (paper: ExamPaper) => {
    console.log("Selected paper:", paper);
    setSelectedPaper(paper);
    setSelectedSection(null);
    toast.info(`Selected paper: ${paper.name}`);
  };

  const handleSectionSelect = (section: ExamSection) => {
    console.log("Selected section:", section);
    setSelectedSection(section);
    toast.info(`Selected section: ${section.name}`);
  };

  const handleLanguageChange = (language: string) => {
    setCurrentLanguage(language);
  };

  const refreshExamData = () => {
    toast.info("Refreshing exam data from database...");
    setSelectedCategory(null);
    setSelectedExam(null);
    setSelectedPaper(null);
    setSelectedSection(null);
    fetchExamCategories();
  };

  const handleGenerateMockTest = async () => {
    if (selectedSection) {
      generateSectionBasedTest();
    } else if (selectedPaper) {
      generatePaperBasedTest();
    } else if (selectedExam) {
      if (selectedExam.papers && selectedExam.papers.length === 1) {
        setSelectedPaper(selectedExam.papers[0]);
        generatePaperBasedTest(selectedExam.papers[0]);
      } else {
        toast.info("Please select a specific paper to generate a test");
      }
    } else if (selectedCategory) {
      generateCategoryBasedTest();
    } else {
      toast.error("Please select a category, exam, or paper first");
    }
  };

  const generateSectionBasedTest = async () => {
    if (!selectedExam || !selectedPaper || !selectedSection) {
      toast.error("Please select an exam, paper, and section first");
      return;
    }
    
    setIsGenerating(true);
    toast.info(`Generating mock test for ${selectedExam.name} - ${selectedPaper.name} - ${selectedSection.name}...`);

    try {
      const mockTestId = generateUUID();
      
      // Fetch exam-specific questions for this section with more specific filtering
      const { data: examQuestions, error: examQuestionsError } = await supabase
        .from('mock_test_questions')
        .select('*')
        .eq('exam_name', selectedExam.name)
        .eq('category', selectedSection.name)
        .limit(selectedSection.questions);
        
      if (examQuestionsError) {
        console.error('Error fetching exam questions:', examQuestionsError);
      }
      
      let questions = examQuestions || [];
      console.log(`Initial query found ${questions.length} questions for ${selectedExam.name} - ${selectedSection.name}`);
      
      if (!questions.length || questions.length < 5) {
        console.log(`Not enough exam-specific questions for ${selectedExam.name} - ${selectedSection.name}, trying general questions`);
        const questionsNeeded = selectedSection.questions;
        
        // Try to get questions that match this specific section name with more precise filtering
        const { data: sectionQuestions, error: sectionError } = await supabase
          .from('questions')
          .select('*')
          .ilike('Category', `%${selectedSection.name}%`)
          .limit(questionsNeeded);
          
        if (sectionError) {
          console.error('Error fetching section questions:', sectionError);
        } else if (sectionQuestions && sectionQuestions.length >= 5) {
          questions = sectionQuestions;
          console.log(`Found ${questions.length} questions matching section "${selectedSection.name}"`);
        } else {
          // Try exact match first
          const { data: exactMatchQuestions, error: exactMatchError } = await supabase
            .from('questions')
            .select('*')
            .eq('Category', selectedSection.name)
            .limit(questionsNeeded);
            
          if (exactMatchError) {
            console.error('Error fetching exact match questions:', exactMatchError);
          } else if (exactMatchQuestions && exactMatchQuestions.length >= 5) {
            questions = exactMatchQuestions;
            console.log(`Found ${questions.length} questions with exact category match "${selectedSection.name}"`);
          } else {
            // If still not enough, try by exam name
            const { data: examBasedQuestions, error: examError } = await supabase
              .from('questions')
              .select('*')
              .ilike('Category', `%${selectedExam.name}%`)
              .limit(questionsNeeded);
              
            if (examError) {
              console.error('Error fetching exam-based questions:', examError);
              toast.error("Failed to fetch questions. Please try again.");
              setIsGenerating(false);
              return;
            }
            
            if (examBasedQuestions && examBasedQuestions.length >= 5) {
              questions = examBasedQuestions;
              console.log(`Found ${questions.length} questions matching exam "${selectedExam.name}"`);
            } else {
              toast.error(`Not enough questions available for the ${selectedSection.name} section. Please try another one or upload more questions.`);
              setIsGenerating(false);
              return;
            }
          }
        }
      }
      
      console.log(`Using ${questions.length} questions for ${selectedExam.name} - ${selectedSection.name} test`);
      
      // Log sample questions to verify content
      if (questions.length > 0) {
        console.log('Sample question categories:');
        questions.slice(0, 3).forEach((q, i) => {
          console.log(`Question ${i+1} category: ${q.category || q.Category || 'Not specified'}`);
          console.log(`Question ${i+1}: ${q.question_text || q.question || q["Question"] || 'Not available'}`);
        });
      }
      
      const formattedQuestions = questions.map(q => {
        const options = [
          q.option_a || q["Option A"] || '',
          q.option_b || q["Option B"] || '',
          q.option_c || q["Option C"] || '',
          q.option_d || q["Option D"] || ''
        ].filter(Boolean);
        
        let correctAnswerIndex = 0;
        const correctAnswer = q.correct_option || q["Correct Answer"] || '';
        
        const contentMatchIndex = options.findIndex(opt => 
          opt.toLowerCase().trim() === correctAnswer.toLowerCase().trim()
        );
        
        if (contentMatchIndex !== -1) {
          correctAnswerIndex = contentMatchIndex;
        } else if (/^[A-D]$/i.test(correctAnswer)) {
          correctAnswerIndex = ['a', 'b', 'c', 'd'].indexOf(correctAnswer.toLowerCase());
          if (correctAnswerIndex === -1) correctAnswerIndex = 0;
        }
        
        const normalizedDifficulty = normalizeDifficulty(q.difficulty || q["Difficulty"] || 'medium');
        
        // Include metadata from original question if available
        const metadata = q.metadata || {};
        
        // Add section information to metadata
        if (!metadata.original_section) {
          metadata.original_section = selectedSection.name;
        }
        
        // Add exam information to the metadata
        metadata.exam_name = selectedExam.name;
        metadata.section_name = selectedSection.name;
        metadata.paper_name = selectedPaper.name;
        
        return {
          id: q.id || q["ID"],
          question: q.question_text || q.question || q["Question"] || '',
          options: options,
          correctanswer: correctAnswerIndex,
          difficulty: normalizedDifficulty,
          category: selectedSection.name, // Explicitly set to selected section name
          subject: selectedExam.name, // Use exam name as subject for better organization
          explanation: q.explanation || q["Explanation"] || `The correct answer is "${options[correctAnswerIndex]}"`,
          question_type: "mcq",
          marks: q.marks || selectedSection.questions > 0 ? (selectedPaper.marks / selectedSection.questions) : 1,
          negative_marks: q.negative_marks || 0.25,
          metadata: metadata
        };
      });
      
      // Clear any previous test configuration that might be cached
      localStorage.removeItem('mockTest_config');
      
      // Create a descriptive title that clearly identifies this test
      const testTitle = `${selectedExam.name} - ${selectedSection.name}`;
      const description = `${selectedExam.name} - ${selectedPaper.name} - ${selectedSection.name} Section`;
      
      // Log the title we're using
      console.log(`Setting test title: "${testTitle}"`);
      console.log(`Setting test description: "${description}"`);
      
      // Create a test configuration with explicit section and exam information
      const testConfig = {
        exam: selectedExam.name,
        examType: selectedExam.type,
        question_count: selectedSection.questions,
        time_minutes: selectedSection.time_minutes,
        has_sections: true,
        sections: [selectedSection],
        description: description,
        testTitle: testTitle, // Explicitly set the test title
        sessionId: mockTestId,
        startTime: new Date().toISOString(),
        timeLimit: selectedSection.time_minutes,
        questions: formattedQuestions,
        language: currentLanguage,
        category: selectedSection.name, // Ensure we set the correct category
      };
      
      console.log(`Generated test config with ${testConfig.questions.length} questions for category=${testConfig.category}`);
      console.log(`Test description: ${testConfig.description}`);
      console.log(`Test title: ${testConfig.testTitle}`);
      
      // Store with test-specific ID first
      localStorage.setItem(`mockTest_${mockTestId}`, JSON.stringify(testConfig));
      
      // Also store in the general config for compatibility
      localStorage.setItem('mockTest_config', JSON.stringify(testConfig));

      const testURL = `/mock-test/${mockTestId}`;
      const testWindow = window.open(
        testURL, 
        'mockTestWindow', 
        'width=1200,height=800,menubar=no,toolbar=no,location=no,resizable=yes,scrollbars=yes,status=no'
      );

      if (testWindow) {
        const checkWindowInterval = setInterval(() => {
          if (testWindow.closed) {
            clearInterval(checkWindowInterval);
            toast.info("Mock test window was closed");
          }
        }, 1000);

        toast.success(`${description} mock test loaded in a new window`);
      } else {
        toast.error("Pop-up blocker prevented the test from opening. Please allow pop-ups for this site.");
        navigate(`/mock-test/${mockTestId}`);
      }
    } catch (error) {
      console.error('Error generating section-based mock test:', error);
      toast.error("Failed to generate mock test. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const generatePaperBasedTest = async (paperToUse?: ExamPaper) => {
    if (!selectedExam) {
      toast.error("Please select an exam first");
      return;
    }
    
    const paperConfig = paperToUse || selectedPaper;
    
    if (!paperConfig) {
      toast.error("No paper configuration available");
      return;
    }

    setIsGenerating(true);
    toast.info(`Generating mock test for ${selectedExam.name} - ${paperConfig.name}...`);

    try {
      const mockTestId = generateUUID();
      
      // Fetch exam-specific questions if available with explicit category filtering
      const { data: examQuestions, error: examQuestionsError } = await supabase
        .from('mock_test_questions')
        .select('*')
        .eq('exam_name', selectedExam.name)
        .limit(paperConfig.question_count);
        
      if (examQuestionsError) {
        console.error('Error fetching exam questions:', examQuestionsError);
      }
      
      let questions = examQuestions || [];
      console.log(`Initial query found ${questions.length} questions for ${selectedExam.name}`);
      
      if (!questions.length || questions.length < 5) {
        const questionsNeeded = paperConfig.question_count;
        
        // Try to get questions with the exact exam name as category
        const { data: exactMatchQuestions, error: exactMatchError } = await supabase
          .from('questions')
          .select('*')
          .eq('Category', selectedExam.name)
          .limit(questionsNeeded);
          
        if (exactMatchError) {
          console.error('Error fetching exact match questions:', exactMatchError);
        } else if (exactMatchQuestions && exactMatchQuestions.length >= 5) {
          questions = exactMatchQuestions;
          console.log(`Found ${questions.length} questions with exact category match "${selectedExam.name}"`);
        } else {
          // Try pattern matching if exact match doesn't return enough
          const { data: patternMatchQuestions, error: patternMatchError } = await supabase
            .from('questions')
            .select('*')
            .ilike('Category', `%${selectedExam.name}%`)
            .limit(questionsNeeded);
            
          if (patternMatchError) {
            console.error('Error fetching pattern match questions:', patternMatchError);
          } else if (patternMatchQuestions && patternMatchQuestions.length >= 5) {
            questions = patternMatchQuestions;
            console.log(`Found ${questions.length} questions with pattern match "${selectedExam.name}"`);
          } else {
            // Fall back to general questions only if necessary
            const { data: generalQuestions, error: generalError } = await supabase
              .from('questions')
              .select('*')
              .limit(questionsNeeded);
              
            if (generalError) {
              console.error('Error fetching general questions:', generalError);
              toast.error("Failed to fetch questions. Please try again.");
              setIsGenerating(false);
              return;
            }
            
            questions = generalQuestions || [];
            console.log(`Falling back to ${questions.length} general questions`);
          }
        }
      }
      
      if (questions.length < 5) {
        toast.error("Not enough questions available for this paper. Please try another one.");
        setIsGenerating(false);
        return;
      }
      
      // Log sample questions for debugging
      if (questions.length > 0) {
        console.log('Sample question categories:');
        questions.slice(0, 3).forEach((q, i) => {
          console.log(`Question ${i+1} category: ${q.category || q.Category || 'Not specified'}`);
          console.log(`Question ${i+1}: ${q.question_text || q.question || q["Question"] || 'Not available'}`);
        });
      }
      
      const formattedQuestions = questions.map(q => {
        const options = [
          q.option_a || q["Option A"] || '',
          q.option_b || q["Option B"] || '',
          q.option_c || q["Option C"] || '',
          q.option_d || q["Option D"] || ''
        ].filter(Boolean);
        
        let correctAnswerIndex = 0;
        const correctAnswer = q.correct_option || q["Correct Answer"] || '';
        
        const contentMatchIndex = options.findIndex(opt => 
          opt.toLowerCase().trim() === correctAnswer.toLowerCase().trim()
        );
        
        if (contentMatchIndex !== -1) {
          correctAnswerIndex = contentMatchIndex;
        } else if (/^[A-D]$/i.test(correctAnswer)) {
          correctAnswerIndex = ['a', 'b', 'c', 'd'].indexOf(correctAnswer.toLowerCase());
          if (correctAnswerIndex === -1) correctAnswerIndex = 0;
        }
        
        const normalizedDifficulty = normalizeDifficulty(q.difficulty || q["Difficulty"] || 'medium');
        
        // Create metadata with exam information
        const metadata = q.metadata || {};
        metadata.exam_name = selectedExam.name;
        metadata.paper_name = paperConfig.name;
        
        // Get the original category and ensure we keep that information
        const originalCategory = q.category || q.Category || selectedExam.name;
        
        return {
          id: q.id || q["ID"],
          question: q.question_text || q.question || q["Question"] || '',
          options: options,
          correctanswer: correctAnswerIndex,
          difficulty: normalizedDifficulty,
          category: originalCategory, // Keep original category for traceability
          subject: selectedExam.name, // Use exam name as subject for consistency
          explanation: q.explanation || q["Explanation"] || `The correct answer is "${options[correctAnswerIndex]}"`,
          question_type: "mcq" as "mcq" | "true_false" | "fill_blank",
          marks: q.marks || paperConfig.marks / paperConfig.question_count || 1,
          negative_marks: q.negative_marks || 0.25,
          metadata: metadata
        };
      });
      
      // Clear any previous test configuration
      localStorage.removeItem('mockTest_config');
      
      // Create a descriptive test title
      const testTitle = selectedExam.name;
      const description = `${selectedExam.name} - ${paperConfig.name}${paperConfig.description ? ` (${paperConfig.description})` : ''}`;
      
      // Log the title and description
      console.log(`Setting test title: "${testTitle}"`);
      console.log(`Setting test description: "${description}"`);
      
      const testConfig = {
        exam: selectedExam.name,
        examType: selectedExam.type,
        question_count: paperConfig.question_count,
        time_minutes: paperConfig.time_minutes,
        has_sections: paperConfig.sections.length > 0,
        sections: paperConfig.sections,
        description: description,
        testTitle: testTitle, // Explicitly set test title
        sessionId: mockTestId,
        startTime: new Date().toISOString(),
        timeLimit: paperConfig.time_minutes,
        questions: formattedQuestions,
        language: currentLanguage,
        category: selectedExam.name, // Set category to exam name for proper identification
      };
      
      console.log(`Generated test config with ${testConfig.questions.length} questions for ${testConfig.category}`);
      console.log(`Test description: ${testConfig.description}`);
      console.log(`Test title: ${testConfig.testTitle}`);
      
      localStorage.setItem(`mockTest_${mockTestId}`, JSON.stringify(testConfig));
      localStorage.setItem('mockTest_config', JSON.stringify(testConfig));

      const testURL = `/mock-test/${mockTestId}`;
      const testWindow = window.open(
        testURL, 
        'mockTestWindow', 
        'width=1200,height=800,menubar=no,toolbar=no,location=no,resizable=yes,scrollbars=yes,status=no'
      );

      if (testWindow) {
        const checkWindowInterval = setInterval(() => {
          if (testWindow.closed) {
            clearInterval(checkWindowInterval);
            toast.info("Mock test window was closed");
          }
        }, 1000);

        toast.success(`${description} mock test loaded in a new window`);
      } else {
        toast.error("Pop-up blocker prevented the test from opening. Please allow pop-ups for this site.");
        navigate(`/mock-test/${mockTestId}`);
      }
    } catch (error) {
      console.error('Error generating mock test:', error);
      toast.error("Failed to generate mock test. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const generateCategoryBasedTest = async () => {
    if (!selectedCategory) {
      toast.error("Please select a category first");
      return;
    }

    setIsGenerating(true);
    toast.info(`Generating mock test for ${selectedCategory}...`);

    try {
      const mockTestId = generateUUID();
      
      const categoryData = categories.find(cat => cat.name === selectedCategory);
      
      if (!categoryData || categoryData.subjects.length === 0) {
        toast.error("No subjects found in this category");
        setIsGenerating(false);
        return;
      }
      
      const categorySubjects = categoryData.subjects;
      console.log(`Mock test will include questions from ${categorySubjects.length} subjects in ${selectedCategory}`);
      
      const questionsPerSubject = Math.ceil(120 / categorySubjects.length);
      let allQuestions = [];
      let formattedQuestions = [];

      try {
        // First try to get questions specifically for this category
        const { data: categoryQuestions, error: categoryError } = await supabase
          .from('questions')
          .select('*')
          .eq('Category', selectedCategory)
          .limit(120);
          
        if (categoryError) {
          console.error(`Error fetching questions for ${selectedCategory}:`, categoryError);
        } else if (categoryQuestions && categoryQuestions.length >= 10) {
          // If we have enough questions directly for the category, use those
          console.log(`Found ${categoryQuestions.length} questions specifically for category ${selectedCategory}`);
          allQuestions = categoryQuestions;
        } else {
          // Otherwise gather questions from all the subjects in this category
          for (const subject of categorySubjects) {
            console.log(`Fetching questions for subject: ${subject}`);
            const { data: subjectQuestions, error } = await supabase
              .from('questions')
              .select('*')
              .eq('Category', subject)
              .limit(questionsPerSubject);
              
            if (error) {
              console.error(`Error fetching questions for ${subject}:`, error);
              continue;
            }
            
            if (subjectQuestions && subjectQuestions.length > 0) {
              allQuestions = [...allQuestions, ...subjectQuestions];
            } else {
              console.log(`No questions found for subject: ${subject}`);
            }
          }
        }
        
        // If we still don't have enough questions, try generating some with the OpenAI function
        if (allQuestions.length < 10) {
          toast.info("Not enough questions in database, generating additional questions...");
          
          try {
            // Call the OpenAI function to generate questions for this category
            const response = await fetch(
              `${window.location.origin}/api/generate-quiz-questions`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  category: selectedCategory,
                  subject: selectedCategory,
                  difficulty: 'mixed',
                  count: 20,
                }),
              }
            );
            
            if (response.ok) {
              const data = await response.json();
              if (data.questions && data.questions.length > 0) {
                console.log(`Generated ${data.questions.length} AI questions for ${selectedCategory}`);
                
                // Format the AI-generated questions to match our expected format
                const aiQuestions = data.questions.map((q, index) => ({
                  "ID": 10000 + index, // Use high IDs to avoid conflicts
                  "Category": selectedCategory,
                  "Question": q.question,
                  "Option A": q.options[0],
                  "Option B": q.options[1],
                  "Option C": q.options[2],
                  "Option D": q.options[3],
                  "Correct Answer": q.options[q.correctanswer], // Use the text of the correct answer
                  "Explanation": q.explanation,
                  "Difficulty": q.difficulty
                }));
                
                allQuestions = [...allQuestions, ...aiQuestions];
              }
            } else {
              console.error("Failed to generate questions via API");
            }
          } catch (genError) {
            console.error("Error generating questions:", genError);
          }
        }
        
        if (allQuestions.length < 10) {
          toast.error(`Not enough questions available for ${selectedCategory}. Please try another category.`);
          setIsGenerating(false);
          return;
        }
        
        allQuestions = allQuestions.sort(() => Math.random() - 0.5).slice(0, 120);
        console.log(`Final mock test has ${allQuestions.length} questions`);
        
        const validQuestions = allQuestions.filter(q => {
          const hasQuestion = q && typeof q.Question === 'string' && q.Question.trim() !== '';
          const hasOptions = q && 
            q["Option A"] && typeof q["Option A"] === 'string' && 
            q["Option B"] && typeof q["Option B"] === 'string' && 
            q["Option C"] && typeof q["Option C"] === 'string' && 
            q["Option D"] && typeof q["Option D"] === 'string';
          const hasCorrectAnswer = q && q["Correct Answer"] && typeof q["Correct Answer"] === 'string';
          
          return hasQuestion && hasOptions && hasCorrectAnswer;
        });
        
        console.log(`Validated ${validQuestions.length} out of ${allQuestions.length} questions`);
        
        if (validQuestions.length < 10) {
          toast.error(`Not enough valid questions available for ${selectedCategory}. Please try another category.`);
          setIsGenerating(false);
          return;
        }
        
        formattedQuestions = validQuestions.map(q => {
          const options = [
            q["Option A"] || 'No option provided',
            q["Option B"] || 'No option provided',
            q["Option C"] || 'No option provided',
            q["Option D"] || 'No option provided'
          ];
          
          let correctAnswerIndex = 0;
          const correctAnswer = q["Correct Answer"].trim();
          
          const contentMatchIndex = options.findIndex(opt => 
            opt.toLowerCase().trim() === correctAnswer.toLowerCase().trim()
          );
          
          if (contentMatchIndex !== -1) {
            correctAnswerIndex = contentMatchIndex;
          } else if (/^[A-D]$/i.test(correctAnswer)) {
            correctAnswerIndex = ['a', 'b', 'c', 'd'].indexOf(correctAnswer.toLowerCase());
            if (correctAnswerIndex === -1) correctAnswerIndex = 0;
          }
          
          const normalizedDifficulty = normalizeDifficulty(q["Difficulty"] || 'medium');
          
          return {
            id: q["ID"],
            question: q["Question"].trim(),
            options: options,
            correctanswer: correctAnswerIndex,
            difficulty: normalizedDifficulty,
            category: selectedCategory, // Explicitly set to the selected category
            subject: q["Category"] || selectedCategory,
            explanation: q["Explanation"] || `The correct answer is "${options[correctAnswerIndex]}"`,
            question_type: 'mcq',
            marks: 1,
            negative_marks: 0.25,
            metadata: {
              exam_name: selectedCategory,
              original_category: q["Category"] || selectedCategory
            }
          };
        });
        
      } catch (queryError) {
        console.error("Error querying questions:", queryError);
        toast.error("Error loading questions. Please try again.");
        setIsGenerating(false);
        return;
      }
      
      // Add debug logging for test config
      console.log(`Creating test for category: ${selectedCategory}`);
      console.log(`Number of questions: ${formattedQuestions.length}`);
      
      // Create a descriptive test title
      const testTitle = selectedCategory;
      const description = `${selectedCategory} Custom Test`;
      
      // Log the title we're using
      console.log(`Setting test title: "${testTitle}"`);
      console.log(`Setting test description: "${description}"`);
      
      const testConfig = {
        category: selectedCategory,
        subjects: categorySubjects,
        sessionId: mockTestId,
        startTime: new Date().toISOString(),
        timeLimit: 120,
        questions: formattedQuestions,
        language: currentLanguage,
        exam: selectedCategory, // Ensure the exam property is explicitly set
        examType: 'custom', // Add exam type for better categorization
        description: description, // Ensure description is explicit
        testTitle: testTitle, // Explicitly set test title
      };
      
      console.log("Generated test config:", testConfig);
      
      // Clear any previous test configuration that might be cached
      localStorage.removeItem('mockTest_config');
      
      // Store with test-specific ID
      localStorage.setItem(`mockTest_${mockTestId}`, JSON.stringify(testConfig));
      
      // Also store in the general config for compatibility
      localStorage.setItem('mockTest_config', JSON.stringify(testConfig));

      const testURL = `/mock-test/${mockTestId}`;
      const testWindow = window.open(
        testURL, 
        'mockTestWindow', 
        'width=1200,height=800,menubar=no,toolbar=no,location=no,resizable=yes,scrollbars=yes,status=no'
      );

      if (testWindow) {
        const checkWindowInterval = setInterval(() => {
          if (testWindow.closed) {
            clearInterval(checkWindowInterval);
            toast.info("Mock test window was closed");
          }
        }, 1000);

        toast.success(`${selectedCategory} mock test loaded in a new window`);
      } else {
        toast.error("Pop-up blocker prevented the test from opening. Please allow pop-ups for this site.");
        navigate(`/mock-test/${mockTestId}`);
      }
    } catch (error) {
      console.error('Error generating mock test:', error);
      toast.error("Failed to generate mock test. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const groupedExams = examCategories.reduce((acc, exam) => {
    if (!acc[exam.type]) {
      acc[exam.type] = [];
    }
    acc[exam.type].push(exam);
    return acc;
  }, {} as Record<string, ExamCategory[]>);

  // Add new functions for Word file processing
  const handleWordFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setDocFile(e.target.files[0]);
      setExtractedQuestions([]);
      setWordUploadSuccess(false);
    }
  };

  const handleProcessDocx = async () => {
    if (!docFile) {
      toast.error("Please select a .docx file first");
      return;
    }

    setExtracting(true);
    setExtractedQuestions([]);

    try {
      const formData = new FormData();
      formData.append("file", docFile);

      // Call our edge function to extract questions & answers
      const { data, error } = await supabase.functions.invoke("process-word-file", {
        body: formData,
        method: "POST"
      });

      if (error || !data) throw error || new Error("No data returned from edge function");

      // Save extracted questions to Supabase
      if (Array.isArray(data.questions) && data.questions.length > 0) {
        for (const q of data.questions) {
          // Save as a mock_test_questions row - with corrected structure
          const questionData = {
            question_text: q.question,
            option_a: q.options[0] || "",
            option_b: q.options[1] || "",
            option_c: q.options[2] || "",
            option_d: q.options[3] || "",
            correct_option: q.correct,
            explanation: q.explanation || "",
            exam_category: "WordImport",  // Required field
            category: "WordImport",       // Required field
            exam_name: "WordImport",      // Required field
            subject: "",                  // Empty for complete papers
            difficulty: "medium",
          };
          
          const { error: insertError } = await supabase
            .from("mock_test_questions")
            .insert([questionData]);
            
          if (insertError) {
            console.error("Error inserting question:", insertError);
            toast.error(`Failed to save question: ${insertError.message}`);
          }
        }
        setExtractedQuestions(data.questions);
        setWordUploadSuccess(true);
        toast.success(`Extracted and saved ${data.questions.length} questions!`);
      } else {
        toast.warning("No questions detected in the document!");
      }
    } catch (err: any) {
      toast.error(`Failed to process file: ${err?.message || "Unknown error"}`);
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className={`container mx-auto ${isMobile ? 'py-3 px-2' : 'py-6'} space-y-${isMobile ? '4' : '6'} ${isMobile ? 'pb-20' : ''}`}>
      {/* Updated PYQ Multiplier Dialog */}
      <Dialog open={showPyqDialog} onOpenChange={setShowPyqDialog}>
        <DialogContent className={`${isMobile ? 'w-[95%] p-4 max-w-[95vw]' : 'sm:max-w-md'}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isMobile ? 'text-lg' : 'text-xl'}`}>
              <span className={`${isMobile ? 'text-xl' : 'text-2xl'}`}>🎯</span> Welcome to your personalized Adaptive Mock Test
            </DialogTitle>
            <DialogDescription className={isMobile ? 'text-sm' : ''}>
              This test evolves in real-time based on your performance, ensuring optimal challenge and coverage.
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg mb-4 space-y-3">
            <p className={`mb-2 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              Powered by PYQ Multiplier, every question is generated by Edaptica itself — built to reflect real exam standards.
              <span className="font-medium block mt-1">Practice only what truly matters.</span>
            </p>
            <p className={isMobile ? 'text-xs' : 'text-sm'}>
              Use the PYQ Multiplier Toggle to customize your test even further!
            </p>
          </div>
          
          <div className="bg-amber-50 p-3 rounded-md text-center border border-amber-100">
            <p className={`text-amber-800 font-medium ${isMobile ? 'text-xs' : ''}`}>
              Adaptive Tests + PYQ Multiplier = Real Exam Advantage
            </p>
          </div>
          
          <DialogFooter className={`flex ${isMobile ? 'flex-col' : 'justify-end'} gap-3 sm:justify-between mt-4`}>
            <Button
              variant="outline"
              onClick={handleClosePyqDialog}
              className={isMobile ? 'w-full' : ''}
            >
              Continue to Mock Tests
            </Button>
            <Button 
              onClick={navigateToPyqMultiplier}
              className={`bg-purple-600 hover:bg-purple-700 ${isMobile ? 'w-full' : ''}`}
            >
              Go to PYQ Multiplier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <div className={`mb-${isMobile ? '4' : '8'} bg-gradient-to-r from-[#9b87f5] via-[#7E69AB] to-[#8860DE] rounded-xl ${isMobile ? 'p-4' : 'p-6'} text-white shadow-lg`}>
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            size={isMobile ? "sm" : "default"}
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className={`mr-2 ${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
            {isMobile ? 'Back' : 'Back to Dashboard'}
          </Button>
          <div className="ml-auto">
            <LanguageSelector 
              currentLanguage={currentLanguage}
              onLanguageChange={handleLanguageChange}
              variant="default"
              className="bg-[#9b87f5]/20 border-white/30 text-white hover:bg-white/20"
              size={isMobile ? "sm" : "default"}
            />
          </div>
        </div>

        <div className="text-center">
          <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl md:text-4xl'} font-bold mb-2 font-playfair`}>Mock Test Generator</h1>
          <p className={`text-white/80 max-w-2xl mx-auto ${isMobile ? 'text-sm' : ''}`}>
            Generate paper-specific mock tests for competitive exams with adaptive difficulty
          </p>
        </div>
      </div>

      <Card className={`${isMobile ? 'p-3' : 'p-5'} mb-${isMobile ? '4' : '8'} border-amber-200 bg-gradient-to-br from-amber-50 to-amber-100/50 shadow-md`}>
        <div className={`flex ${isMobile ? 'flex-col' : 'items-start'} gap-${isMobile ? '2' : '4'}`}>
          <div className="p-3 rounded-full bg-amber-100 text-amber-700">
            <AlertTriangle className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
          </div>
          <div>
            <h3 className={`font-medium text-amber-800 ${isMobile ? 'text-base' : 'text-lg'} mb-2`}>Important Instructions</h3>
            <ul className={`${isMobile ? 'text-xs' : 'text-sm'} text-amber-700 space-y-1.5`}>
              <li className="flex items-start gap-2">
                <div className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} flex items-center justify-center`}>•</div>
                <span>Select an exam category and specific paper to generate a tailored mock test</span>
              </li>
              <li className="flex items-start gap-2">
                <div className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} flex items-center justify-center`}>•</div>
                <span>For exams with sections, you can select a specific section to test separately</span>
              </li>
              {!isMobile && (
                <>
                  <li className="flex items-start gap-2">
                    <div className="h-5 w-5 flex items-center justify-center">•</div>
                    <span>Time limits are enforced based on the selected paper specifications</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="h-5 w-5 flex items-center justify-center">•</div>
                    <span>Do not navigate away from the test page during the test</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="h-5 w-5 flex items-center justify-center">•</div>
                    <span>Ensure you have a stable internet connection</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="h-5 w-5 flex items-center justify-center">•</div>
                    <span>Marking schemes vary by exam: typically -0.25 for wrong answers</span>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      </Card>

      <Card className={`mb-${isMobile ? '4' : '8'} overflow-hidden border shadow-md`}>
        <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100/50 pb-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                <FilePlus className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
              </div>
              <CardTitle className={`${isMobile ? 'text-base' : 'text-lg'} font-medium`}>Question Import</CardTitle>
            </div>
            <Button 
              variant="outline" 
              size={isMobile ? "sm" : "default"}
              onClick={refreshExamData}
              className="flex items-center gap-1.5 shadow-sm"
            >
              <RefreshCw className={`${isMobile ? 'h-3 w-3' : 'h-3.5 w-3.5'}`} />
              {isMobile ? 'Refresh' : 'Refresh Data'}
            </Button>
          </div>
          <CardDescription className={`text-sm text-gray-600 ml-9 ${isMobile ? 'hidden' : ''}`}>
            Import questions from Anki decks or Word files into your exam categories
          </CardDescription>
        </CardHeader>
        <CardContent className={`${isMobile ? 'p-3' : 'pt-5 pb-6'}`}>
          <div className="flex space-x-2 mb-4">
            <Button 
              variant={questionImportTab === "anki" ? "default" : "outline"}
              onClick={() => setQuestionImportTab("anki")}
              size={isMobile ? "sm" : "default"}
            >
              <BookOpen className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} mr-1`}/> 
              {isMobile ? 'Anki' : 'Import from Anki'}
            </Button>
            <Button 
              variant={questionImportTab === "word" ? "default" : "outline"}
              onClick={() => setQuestionImportTab("word")}
              size={isMobile ? "sm" : "default"}
            >
              <Upload className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} mr-1`}/> 
              {isMobile ? 'Word' : 'Import Word .docx'}
            </Button>
          </div>
          {questionImportTab === "anki" && (
            <div className="space-y-4">
              <MockTestAnkiImport 
                selectedExam={selectedExam}
                selectedSection={selectedSection}
                onImport={fetchExamCategories}
              />
            </div>
          )}
          {questionImportTab === "word" && (
            <div className={`mb-2 ${isMobile ? 'text-sm' : ''}`}>
              <label className="block font-medium mb-2">
                {isMobile ? 'Upload .docx with MCQ questions' : 'Upload .docx file with MCQ questions and ANSWERS (marked \'ANSWER:\' in text)'}
              </label>
              <input
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleWordFileChange}
                className="mb-2 block"
                disabled={extracting}
              />
              <Button
                onClick={handleProcessDocx}
                disabled={extracting || !docFile}
                className="mb-4"
                size={isMobile ? "sm" : "default"}
              >
                {extracting ? (
                  <>
                    <Loader2 className={`${isMobile ? 'h-3 w-3 mr-1' : 'h-4 w-4 mr-2'} animate-spin`} />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className={`${isMobile ? 'h-3 w-3 mr-1' : 'h-4 w-4 mr-2'}`} />
                    {isMobile ? 'Extract' : 'Upload & Extract'}
                  </>
                )}
              </Button>
              {wordUploadSuccess && extractedQuestions.length > 0 && (
                <div className={`mt-2 space-y-1 bg-green-50 p-2 rounded ${isMobile ? 'text-xs' : ''}`}>
                  <strong>Preview of extracted questions:</strong>
                  <ol className="list-decimal pl-5">
                    {extractedQuestions.slice(0, isMobile ? 3 : 5).map((q, idx) => (
                      <li key={idx}>
                        <span className="font-medium">{q.question}</span>
                        <div className="ml-4">
                          {q.options && q.options.map((opt: string, i: number) => (
                            <div key={i} className={isMobile ? 'text-[10px]' : 'text-xs'}>{String.fromCharCode(65 + i)}. {opt}</div>
                          ))}
                          <div className="text-green-700 text-xs mt-1">
                            Correct: {q.correct}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                  {extractedQuestions.length > (isMobile ? 3 : 5) && (
                    <div className="mt-2 text-xs text-gray-500">...and {extractedQuestions.length - (isMobile ? 3 : 5)} more</div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {paperLoadError && (
        <Card className={`mb-${isMobile ? '4' : '8'} border-red-200 bg-gradient-to-br from-red-50 to-red-100/50 shadow-md overflow-hidden`}>
          <CardContent className={isMobile ? 'p-3' : 'p-4'}>
            <div className={`flex ${isMobile ? 'flex-col' : 'items-start'} gap-3`}>
              <div className="p-2.5 rounded-full bg-red-100 text-red-600">
                <AlertTriangle className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
              </div>
              <div>
                <h3 className={`font-medium text-red-800 mb-1 ${isMobile ? 'text-base' : 'text-lg'}`}>Error Loading Papers</h3>
                <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-red-700 mb-3`}>{paperLoadError}</p>
                <Button 
                  variant="outline" 
                  size={isMobile ? "sm" : "default"}
                  onClick={refreshExamData}
                  className="border-red-200 text-red-700 hover:text-red-800 hover:bg-red-100"
                >
                  <RefreshCw className={`mr-2 ${isMobile ? 'h-3 w-3' : 'h-3.5 w-3.5'}`} />
                  Try Again
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border shadow-md overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-100/50 pb-3">
          <CardTitle className={`${isMobile ? 'text-base' : 'text-lg'} font-medium flex items-center gap-2`}>
            <Sparkles className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-purple-500`} />
            Test Selection
          </CardTitle>
          <CardDescription className={isMobile ? 'text-xs' : ''}>
            Choose from standardized exams or create custom tests from categories
          </CardDescription>
        </CardHeader>
        <CardContent className={isMobile ? 'p-3' : 'p-6'}>
          <Tabs defaultValue="exams" className="mb-6">
            <TabsList className={`w-full bg-gray-100/80 p-1 rounded-lg mb-${isMobile ? '4' : '6'}`}>
              <TabsTrigger value="exams" className="flex-1 py-2 rounded-md">
                <GraduationCap className={`${isMobile ? 'h-3 w-3 mr-1' : 'h-4 w-4 mr-2'}`} />
                {isMobile ? 'Exams' : 'Standard Exams'}
              </TabsTrigger>
              <TabsTrigger value="categories" className="flex-1 py-2 rounded-md">
                <BookOpen className={`${isMobile ? 'h-3 w-3 mr-1' : 'h-4 w-4 mr-2'}`} />
                {isMobile ? 'Categories' : 'Custom Categories'}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="exams" className="pt-2 space-y-6">
              <div className={`flex mb-5 overflow-x-auto gap-2 pb-2 ${isMobile ? 'flex-nowrap' : ''}`}>
                <Button 
                  variant={examTypeFilter === "government" ? "default" : "outline"} 
                  onClick={() => setExamTypeFilter("government")}
                  size={isMobile ? "sm" : "default"}
                  className={cn(
                    "shadow-sm whitespace-nowrap",
                    examTypeFilter === "government" && "bg-[#9b87f5] hover:bg-[#7E69AB]"
                  )}
                >
                  {isMobile ? 'Govt' : 'Government & Competitive'}
                </Button>
                <Button 
                  variant={examTypeFilter === "school" ? "default" : "outline"} 
                  onClick={() => setExamTypeFilter("school")}
                  size={isMobile ? "sm" : "default"}
                  className={cn(
                    "shadow-sm whitespace-nowrap",
                    examTypeFilter === "school" && "bg-[#9b87f5] hover:bg-[#7E69AB]"
                  )}
                >
                  {isMobile ? 'School' : 'School & Board'}
                </Button>
                <Button 
                  variant={examTypeFilter === "entrance" ? "default" : "outline"} 
                  onClick={() => setExamTypeFilter("entrance")}
                  size={isMobile ? "sm" : "default"}
                  className={cn(
                    "shadow-sm whitespace-nowrap",
                    examTypeFilter === "entrance" && "bg-[#9b87f5] hover:bg-[#7E69AB]"
                  )}
                >
                  {isMobile ? 'Entrance' : 'Entrance Exams'}
                </Button>
              </div>
              
              {isLoading ? (
                <div className="text-center p-12">
                  <p>Loading exams...</p>
                </div>
              ) : groupedExams[examTypeFilter] && groupedExams[examTypeFilter].length > 0 ? (
                <div className={`grid grid-cols-1 ${isMobile ? '' : 'md:grid-cols-2 lg:grid-cols-3'} gap-${isMobile ? '2' : '4'}`}>
                  {groupedExams[examTypeFilter].map((exam) => (
                    <Card
                      key={exam.id}
                      className={cn(
                        "border overflow-hidden transition-all duration-200 hover:shadow-md",
                        selectedExam?.id === exam.id && "border-[#9b87f5] bg-purple-50/50 ring-1 ring-[#9b87f5]/50"
                      )}
                      onClick={() => handleExamSelect(exam)}
                    >
                      <CardHeader className={`${isMobile ? 'p-3' : 'pb-2'}`}>
                        <CardTitle className={`${isMobile ? 'text-sm' : 'text-base'} flex justify-between items-center`}>
                          <span>{exam.name}</span>
                          {exam.has_sections && (
                            <span className={`bg-purple-100 text-purple-700 ${isMobile ? 'text-[8px] px-1.5 py-0.5' : 'text-[10px] px-2 py-0.5'} font-normal rounded-full`}>
                              Sectional
                            </span>
                          )}
                        </CardTitle>
                        <CardDescription className={isMobile ? 'text-[10px]' : 'text-xs'}>
                          {exam.description || `${exam.question_count} questions • ${exam.time_minutes} minutes`}
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className={`text-center ${isMobile ? 'p-4' : 'p-8'} bg-gray-50 rounded-lg border border-dashed border-gray-300`}>
                  <p className="text-gray-500">No exams available in this category</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="categories" className="pt-2">
              <div className={`grid grid-cols-1 ${isMobile ? 'grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'} gap-${isMobile ? '2' : '4'}`}>
                {categories.map((category) => (
                  <Card
                    key={category.name}
                    className={cn(
                      "border overflow-hidden transition-all duration-200 hover:shadow-md",
                      selectedCategory === category.name && "border-[#9b87f5] bg-purple-50/50 ring-1 ring-[#9b87f5]/50"
                    )}
                    onClick={() => handleCategorySelect(category.name)}
                  >
                    <CardHeader className={`${isMobile ? 'p-3' : 'pb-2'}`}>
                      <CardTitle className={isMobile ? 'text-sm' : 'text-base'}>{category.name}</CardTitle>
                      <CardDescription className={isMobile ? 'text-[10px]' : 'text-xs'}>
                        {category.subjects.length} subjects • General test
                      </CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {selectedExam && (
        <Card className={`mt-${isMobile ? '4' : '8'} border shadow-md overflow-hidden`}>
          <CardHeader className="bg-gradient-to-r from-[#9b87f5]/10 via-[#9b87f5]/5 to-white pb-4">
            <div className="flex justify-between">
              <CardTitle className={`${isMobile ? 'text-base' : 'text-lg'} font-medium`}>{selectedExam.name}</CardTitle>
              <Button 
                variant="outline" 
                size={isMobile ? "sm" : "default"}
                onClick={() => setSelectedExam(null)}
                className={`${isMobile ? 'text-xs h-7 px-2' : ''}`}
              >
                Change
              </Button>
            </div>
            <CardDescription className={isMobile ? 'text-xs' : ''}>
              {selectedExam.description || `${selectedExam.question_count} questions in ${selectedExam.time_minutes} minutes`}
            </CardDescription>
          </CardHeader>
          
          <CardContent className={isMobile ? 'p-3' : 'p-6'}>
            {selectedExam.papers && selectedExam.papers.length > 1 && (
              <div className={`mb-${isMobile ? '4' : '6'}`}>
                <h4 className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium mb-3 text-gray-700`}>Select Paper:</h4>
                <div className={`grid grid-cols-1 ${isMobile ? '' : 'sm:grid-cols-2'} gap-${isMobile ? '2' : '3'}`}>
                  {selectedExam.papers.map((paper) => (
                    <Card
                      key={paper.id}
                      className={cn(
                        "border cursor-pointer",
                        selectedPaper?.id === paper.id && "border-[#9b87f5] bg-purple-50/50 ring-1 ring-[#9b87f5]/50"
                      )}
                      onClick={() => handlePaperSelect(paper)}
                    >
                      <CardHeader className={isMobile ? 'p-3' : 'p-4'}>
                        <CardTitle className={isMobile ? 'text-sm' : 'text-base'}>{paper.name}</CardTitle>
                        <CardDescription className={isMobile ? 'text-[10px]' : 'text-xs'}>
                          {paper.description || `${paper.question_count} questions • ${paper.time_minutes} minutes`}
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              </div>
            )}
            
            {selectedPaper && selectedPaper.sections && selectedPaper.sections.length > 0 && (
              <div className={`mb-${isMobile ? '4' : '6'}`}>
                <h4 className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium mb-3 text-gray-700`}>
                  {selectedSection ? "Selected Section:" : "Select Section:"}
                </h4>
                <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'} gap-${isMobile ? '2' : '3'}`}>
                  {selectedPaper.sections.map((section) => (
                    <Card
                      key={section.name}
                      className={cn(
                        "border cursor-pointer",
                        selectedSection?.name === section.name && "border-[#9b87f5] bg-purple-50/50 ring-1 ring-[#9b87f5]/50"
                      )}
                      onClick={() => handleSectionSelect(section)}
                    >
                      <CardHeader className={isMobile ? 'p-2' : 'p-3'}>
                        <CardTitle className={isMobile ? 'text-xs' : 'text-sm'}>{section.name}</CardTitle>
                        <CardDescription className={isMobile ? 'text-[10px]' : 'text-xs'}>
                          {section.questions} questions • {section.time_minutes} min
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
                {selectedSection && (
                  <Button
                    variant="outline"
                    size={isMobile ? "sm" : "default"}
                    onClick={() => setSelectedSection(null)}
                    className="mt-3"
                  >
                    {isMobile ? 'Full Paper' : 'Test Full Paper Instead'}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
          
          <CardFooter className={`border-t ${isMobile ? 'p-3' : 'p-4'} bg-gray-50/80`}>
            <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-3 w-full`}>
              <Button
                variant="default"
                onClick={handleGenerateMockTest}
                disabled={isGenerating}
                className={`${isMobile ? '' : 'sm:flex-1'} bg-[#9b87f5] hover:bg-[#8860DE]`}
              >
                {isGenerating ? (
                  "Generating Test..."
                ) : selectedSection ? (
                  `Generate ${selectedSection.name} Test`
                ) : selectedPaper ? (
                  `Generate ${selectedPaper.name} Test`
                ) : (
                  `Generate ${selectedExam.name} Test`
                )}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setSelectedExam(null);
                  setSelectedPaper(null);
                  setSelectedSection(null);
                }}
                className={isMobile ? '' : 'sm:w-auto'}
              >
                Cancel
              </Button>
            </div>
          </CardFooter>
        </Card>
      )}
      
      {selectedCategory && !selectedExam && (
        <Card className={`mt-${isMobile ? '4' : '8'} border shadow-md overflow-hidden`}>
          <CardHeader className="bg-gradient-to-r from-[#9b87f5]/10 via-[#9b87f5]/5 to-white pb-4">
            <div className="flex justify-between">
              <CardTitle className={`${isMobile ? 'text-base' : 'text-lg'} font-medium`}>{selectedCategory}</CardTitle>
              <Button 
                variant="outline" 
                size={isMobile ? "sm" : "default"}
                onClick={() => setSelectedCategory(null)}
                className={`${isMobile ? 'text-xs h-7 px-2' : ''}`}
              >
                Change
              </Button>
            </div>
            <CardDescription className={isMobile ? 'text-xs' : ''}>
              Custom test with questions from all subjects
            </CardDescription>
          </CardHeader>
          
          <CardContent className={isMobile ? 'p-3' : 'p-6'}>
            <div className="flex flex-col gap-3">
              <div className={`bg-gray-50 rounded-lg ${isMobile ? 'p-3' : 'p-4'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Layers className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} text-gray-500`} />
                  <h4 className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700`}>Subjects Included</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {categories.find(c => c.name === selectedCategory)?.subjects.map((subject) => (
                    <span 
                      key={subject} 
                      className={`${isMobile ? 'text-[10px]' : 'text-xs'} bg-gray-100 px-2 py-1 rounded-full text-gray-600`}
                    >
                      {subject}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className={`bg-gray-50 rounded-lg ${isMobile ? 'p-3' : 'p-4'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <ClockIcon className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} text-gray-500`} />
                  <h4 className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700`}>Test Configuration</h4>
                </div>
                <div className={`grid grid-cols-2 gap-x-4 gap-y-2 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  <div className="text-gray-500">Questions</div>
                  <div className="font-medium">120 questions</div>
                  <div className="text-gray-500">Time Limit</div>
                  <div className="font-medium">120 minutes</div>
                  <div className="text-gray-500">Difficulty</div>
                  <div className="font-medium">Mixed</div>
                </div>
              </div>
            </div>
          </CardContent>
          
          <CardFooter className={`border-t ${isMobile ? 'p-3' : 'p-4'} bg-gray-50/80`}>
            <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-3 w-full`}>
              <Button
                variant="default"
                onClick={handleGenerateMockTest}
                disabled={isGenerating || (!selectedCategory && !selectedExam)}
                className={`${isMobile ? '' : 'sm:flex-1'} bg-[#9b87f5] hover:bg-[#8860DE]`}
              >
                {isGenerating ? "Generating Test..." : `Generate ${selectedCategory} Test`}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setSelectedCategory(null)}
                className={isMobile ? '' : 'sm:w-auto'}
              >
                Cancel
              </Button>
            </div>
          </CardFooter>
        </Card>
      )}
      
      {!selectedCategory && !selectedExam && (
        <div className={`mt-${isMobile ? '4' : '8'} flex justify-center`}>
          <Button
            onClick={handleGenerateMockTest}
            disabled={isGenerating || (!selectedCategory && !selectedExam)}
            className={`${isMobile ? 'px-6 py-4 text-base' : 'px-8 py-6 text-lg'} bg-[#9b87f5] hover:bg-[#8860DE] shadow-md`}
          >
            {isGenerating ? "Generating Test..." : "Select a Test Category"}
          </Button>
        </div>
      )}
      
      {activeView === "upload" && (
        <Card>
          <CardHeader>
            <CardTitle className={isMobile ? 'text-base' : 'text-lg'}>Upload Question Paper</CardTitle>
          </CardHeader>
          <CardContent>
            <PaperUploader 
              onSuccess={() => {
                fetchExamCategories();
                setActiveView("select");
              }}
              selectedExam={selectedExam}
              selectedSection={selectedSection}
            />
          </CardContent>
        </Card>
      )}
      
      {activeView === "select" && (
        <div className={`fixed ${isMobile ? 'bottom-20' : 'bottom-4'} right-4 z-50`}>
          <Button 
            variant="default" 
            size="icon" 
            className={`rounded-full shadow-lg bg-[#9b87f5] hover:bg-[#8860DE] ${isMobile ? 'h-12 w-12' : ''}`}
            onClick={() => setActiveView("upload")}
          >
            <FilePlus className={isMobile ? 'h-5 w-5' : 'h-6 w-6'} />
          </Button>
        </div>
      )}
    </div>
  );
};

export default MockTestGenerator;
